import React, { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import {
    Card,
    CardContent,
    Typography,
    Grid,
} from '@mui/material';
import SankeyChartSection from '../../SankeyChartSection';

const ApplicantCharts = ({ applicantData }) => {
    const statusPieOptions = useMemo(() => {
        if (!applicantData) return {};
        const statusCounts = applicantData.reduce((acc, applicant) => {
            const status = applicant.Activity || 'Pending';
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});
        const statusPieData = Object.keys(statusCounts).map(status => ({
            name: status, y: statusCounts[status],
        }));

        return {
            chart: { type: 'pie' },
            title: { text: 'Applicant Status Distribution' },
            tooltip: { pointFormat: '<b>{point.y}</b> applicant(s) ({point.percentage:.1f}%)' },
            plotOptions: {
                pie: { allowPointSelect: true, cursor: 'pointer', dataLabels: { enabled: true, format: '<b>{point.name}</b>: {point.y}' } },
            },
            series: [{ name: 'Status', colorByPoint: true, data: statusPieData }],
            credits: { enabled: false },
        };
    }, [applicantData]);

    const stageOptions = useMemo(() => {
        if (!applicantData) return {};
        const stageOrder = ['Contacted', 'Scheduled', 'Interviewed', 'Offered', 'Selected', 'OfferRefused', 'NotSelected', 'NotConsidered', 'Pending'];
        // Final disposition comes from StatusCode (same codes as getTabByStatus/showStatusAssigned
        // elsewhere in CertificationListTwo.jsx). Earlier-stage progress that has no StatusCode yet
        // falls back to the boolean flags used by CertificationTrelloBoard.getLastTrueActivity.
        const getStage = (applicant) => {
            // Trimmed - a padded code ("W ") is still its status.
            switch (String(applicant.StatusCode ?? "").trim()) {
                case 'DNA': case 'DEC': case 'W': case 'REJ': case 'SEL,REF':
                    return 'NotConsidered';
                case 'NS':
                    return 'NotSelected';
                case 'SEL': case 'REF':
                    return 'Selected';
                // Offer sent and awaiting a reply / refused by the applicant -
                // same codes the card chip shows as "Offered" / "Refused to be
                // Selected". Without these they fell through to "Interviewed".
                case 'OFR':
                    return 'Offered';
                case 'OFR,REF':
                    return 'OfferRefused';
            }
            // Same outcomes from the flags, for rows whose StatusCode isn't set
            // (an accepted offer keeps Offer=true, so Selected goes first).
            if (applicant.Selected) return 'Selected';
            if (applicant.OfferRejected) return 'OfferRefused';
            if (applicant.Offer) return 'Offered';
            if (applicant.Interviewed) return 'Interviewed';
            if (applicant.Scheduled || applicant.ScheduledDate) return 'Scheduled';
            if (applicant.Contacted) return 'Contacted';
            return 'Pending';
        };

        const rawStageCounts = applicantData.reduce((acc, applicant) => {
            const stage = getStage(applicant);
            acc[stage] = (acc[stage] || 0) + 1;
            return acc;
        }, {});
        const stageCounts = stageOrder.map(stage => rawStageCounts[stage] || 0);

        return {
            chart: { type: 'column' },
            title: { text: 'Applicants by Hiring Stage' },
            xAxis: { categories: stageOrder },
            yAxis: { min: 0, title: { text: 'Number of Applicants' }, allowDecimals: false },
            legend: { enabled: false },
            series: [{ name: 'Applicants', data: stageCounts, colorByPoint: true }],
            credits: { enabled: false },
        };
    }, [applicantData]);

    if (!applicantData || applicantData.length === 0) {
        return (
            <Typography variant="subtitle1" align="center" sx={{ my: 3 }}>
                No data available to display charts.
            </Typography>
        );
    }

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <HighchartsReact highcharts={Highcharts} options={statusPieOptions} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <HighchartsReact highcharts={Highcharts} options={stageOptions} />
                    </Grid>
                    {/* <Grid item xs={12}>
                        <SankeyChartSection applicantData={applicantData} />
                    </Grid> */}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default ApplicantCharts;
